import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  AUTH_TOKEN,
  TWO_FA_STATUS,
  MESSAGE_CODE_2FA_REQUIRED,
} from "constants/AuthConstant";
import AuthService from "services/AuthService";
import MasterService from "services/MasterService";
import {
  ENABLE_SAAS_FEATURE,
  DEFAULT_ORGANIZATION_ID,
} from "configs/AppConfig";
import { resolveDefaultOrganization } from "utils/commonMethods";

const writeTwoFactorStatus = (status) => {
  if (status === null || status === undefined) {
    localStorage.removeItem(TWO_FA_STATUS);
    return;
  }
  localStorage.setItem(TWO_FA_STATUS, JSON.stringify(status));
};

export const initialState = {
  loading: true,
  message: "",
  showMessage: false,
  redirect: "",
  expiresAt: null,
  isAuthenticated: false,
  token: localStorage.getItem(AUTH_TOKEN) || null,
  userData: null,
  lawFirmId: null,
  organizationId: null,
  unsavedChangesChecker: null,
  organizationRefreshTrigger: 0,
  twoFactor: {
    required: false,
    tempToken: null,
    method: null,
  },
};

/*
Author: vipul.singh@velsof.com
Purpose: Validate login by refreshing token
*/
export const validateLogin = createAsyncThunk(
  "auth/refresh",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.refreshToken();
      if (response.status === true) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        return {
          status: response.status,
          expiresAt,
        };
      } else {
        return rejectWithValue(response?.data?.message || "Error");
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign In
*/
export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.login({ data });
      if (response.status === true) {
        // 2FA required — server returns temp_token + two_fa_method
        if (response.message_code === MESSAGE_CODE_2FA_REQUIRED) {
          return {
            twoFactorRequired: true,
            tempToken: response.data?.temp_token,
            method: response.data?.two_fa_method,
          };
        }
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        dispatch(fetchWhoami());
        return {
          user: response.data.user,
          expiresAt,
        };
      }
      return rejectWithValue(response.message || "Login failed");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

/*
Purpose: Verify 2FA code to complete login
*/
export const verify2FALogin = createAsyncThunk(
  "auth/verify2FALogin",
  async ({ tempToken, totp }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.verify2FALogin({
        data: { temp_token: tempToken, totp },
      });
      if (response.status === true) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        dispatch(fetchWhoami());
        return {
          user: response.data.user,
          expiresAt,
        };
      }
      return rejectWithValue(response.message || "2FA verification failed");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "2FA verification failed",
      );
    }
  },
);

/*
Purpose: Verify 2FA backup code to complete login (app method)
*/
export const verify2FALoginBackupCode = createAsyncThunk(
  "auth/verify2FALoginBackupCode",
  async ({ tempToken, backupCode }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.verify2FALoginBackupCode({
        data: { temp_token: tempToken, backup_code: backupCode },
      });
      if (response.status === true) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        dispatch(fetchWhoami());
        return {
          user: response.data.user,
          expiresAt,
        };
      }
      return rejectWithValue(response.message || "2FA verification failed");
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "2FA verification failed",
      );
    }
  },
);

/*
Purpose: Resend 2FA email OTP during login using the existing temp_token
*/
export const resend2FALoginOtp = createAsyncThunk(
  "auth/resend2FALoginOtp",
  async ({ tempToken }, { rejectWithValue }) => {
    try {
      const response = await AuthService.resend2FALoginOtp({
        data: { temp_token: tempToken },
      });
      if (response.status === true) {
        return { message: response.message };
      }
      return rejectWithValue(
        response.message || "Failed to resend verification code",
      );
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to resend verification code",
      );
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign Up, current not in use
*/
export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.register({ email, password });
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign Out
*/
export const signOut = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.logout();

      if (response.status) {
        return {
          status: response,
        };
      } else {
        return rejectWithValue("Logout failed");
      }
    } catch (error) {
      return rejectWithValue(error.response?.message || "Error");
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign In with Google OAuth, currently not in use
*/
export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign In with Facebook OAuth, currently not in use
*/
export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: Fetch current user details
*/
export const fetchWhoami = createAsyncThunk(
  "auth/whoami",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.whoami();
      if (response.status === true) {
        let userData = response.data;

        // If SaaS feature is disabled and whoami didn't return any orgs, resolve default organization
        if (!ENABLE_SAAS_FEATURE && !userData.organisations?.length) {
          const defaultOrg = await resolveDefaultOrganization(
            MasterService.getOrganisations,
            DEFAULT_ORGANIZATION_ID,
          );

          if (defaultOrg) {
            userData = {
              ...userData,
              organisations: [defaultOrg],
            };
          }
        }

        return userData;
      }
      return rejectWithValue(response.message || "Failed to fetch user data");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.resetPassword({ data });
      if (response.status === true) {
        return {
          status: response.status,
          message: response.message,
        };
      }
      return rejectWithValue(response.message || "Password Reset Failed!");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

/*
Author: vipul.singh@velsof.com
Purpose: Creating auth slice for Redux Toolkit, handling authentication state and extra reducers
*/
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
      state.isAuthenticated = true;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    setUserData: (state, action) => {
      state.userData = action.payload;
    },
    setOrganizationId: (state, action) => {
      state.organizationId = action.payload;
    },
    setUnsavedChangesChecker: (state, action) => {
      state.unsavedChangesChecker = action.payload;
    },
    refreshOrganizations: (state) => {
      state.organizationRefreshTrigger += 1;
    },
    resetTwoFactor: (state) => {
      state.twoFactor = {
        required: false,
        tempToken: null,
        method: null,
      };
    },
    setTwoFactorStatus: (state, action) => {
      const { is_2fa_enabled, two_fa_method = null } = action.payload || {};
      if (state.userData) {
        state.userData = {
          ...state.userData,
          is_2fa_enabled: Boolean(is_2fa_enabled),
          two_fa_method: is_2fa_enabled ? two_fa_method : null,
        };
      }
      writeTwoFactorStatus({
        is_2fa_enabled: Boolean(is_2fa_enabled),
        two_fa_method: is_2fa_enabled ? two_fa_method : null,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.twoFactorRequired) {
          state.twoFactor = {
            required: true,
            tempToken: action.payload.tempToken,
            method: action.payload.method,
          };
          writeTwoFactorStatus({
            is_2fa_enabled: true,
            two_fa_method: action.payload.method || null,
          });
          return;
        }
        state.redirect = "/";
        state.token = action.payload;
        state.expiresAt = action?.payload?.expiresAt;
        state.isAuthenticated = true;
        state.twoFactor = {
          required: false,
          tempToken: null,
          method: null,
        };
        writeTwoFactorStatus({ is_2fa_enabled: false, two_fa_method: null });
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(verify2FALogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(verify2FALogin.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
        state.expiresAt = action?.payload?.expiresAt;
        state.isAuthenticated = true;
        writeTwoFactorStatus({
          is_2fa_enabled: true,
          two_fa_method: state.twoFactor?.method || null,
        });
        state.twoFactor = {
          required: false,
          tempToken: null,
          method: null,
        };
      })
      .addCase(verify2FALogin.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(verify2FALoginBackupCode.pending, (state) => {
        state.loading = true;
      })
      .addCase(verify2FALoginBackupCode.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
        state.expiresAt = action?.payload?.expiresAt;
        state.isAuthenticated = true;
        writeTwoFactorStatus({
          is_2fa_enabled: true,
          two_fa_method: state.twoFactor?.method || null,
        });
        state.twoFactor = {
          required: false,
          tempToken: null,
          method: null,
        };
      })
      .addCase(verify2FALoginBackupCode.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.redirect = "/";
        state.userData = null;
        state.lawFirmId = null;
        state.organizationId = null;
        state.twoFactor = {
          required: false,
          tempToken: null,
          method: null,
        };
        writeTwoFactorStatus(null);
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
        state.userData = null;
        state.lawFirmId = null;
        state.organizationId = null;
        state.twoFactor = {
          required: false,
          tempToken: null,
          method: null,
        };
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(validateLogin.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.expiresAt = action?.payload?.expiresAt;
        state.loading = false;
      })
      .addCase(validateLogin.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.expiresAt = null;
        state.loading = false;
      })
      .addCase(fetchWhoami.pending, (state) => {})
      .addCase(fetchWhoami.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.userData = payload;
        const lawFirm = payload?.law_firms?.[0];
        state.lawFirmId = lawFirm?.id;

        // Only set organizationId if not already set (e.g., from URL param)
        // This allows NavOrganization to set it from URL before this runs
        if (!state.organizationId) {
          const organisation = payload?.organisations?.[0];
          state.organizationId = organisation?.id;
        }
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.message = action.payload.message;
        state.redirect = "/";
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      });
  },
});

/*
Author: vipul.singh@velsof.com
Purpose: Exporting actions and reducer from auth slice
*/
export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  setUserData,
  setOrganizationId,
  setUnsavedChangesChecker,
  refreshOrganizations,
  resetTwoFactor,
  setTwoFactorStatus,
} = authSlice.actions;

export default authSlice.reducer;
